import type { MouseEvent as ReactMouseEvent, ReactElement, ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyState, ErrorState } from './EmptyState';

/**
 * The table.
 *
 * A ledger, not a grid: hairline rules, no zebra striping, no card. Numeric
 * columns are right-aligned and tabular so the digits stack; identifier columns
 * are monospace. Header row is sticky, because a fleet list is scrolled.
 *
 * Every table gets a designed empty state and a designed error state, and both
 * are required arguments rather than optional niceties — a table that renders
 * nothing when the request fails is how staff lose trust in the whole screen.
 */

export interface Column<T> {
  readonly key: string;
  readonly header: string;
  /** Right-aligned and tabular. Money, counts, kilometres. */
  readonly numeric?: boolean;
  /** Hidden below `sm`. For columns that are useful but not essential. */
  readonly secondary?: boolean;
  readonly render: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  readonly columns: readonly Column<T>[];
  readonly rows: readonly T[];
  readonly rowKey: (row: T) => string;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly onRetry?: () => void;
  readonly errorHeadline: string;
  readonly errorDetail: string;
  readonly empty: { readonly headline: string; readonly detail: string; readonly action?: ReactNode };
  /**
   * Where a row goes when opened.
   *
   * The whole row is clickable, and the first cell is still a real link. Both,
   * on purpose: the row is the target a hand goes for, and the link is what a
   * keyboard tabs to and a screen reader announces. A row handler on its own
   * would be unreachable without a mouse.
   */
  readonly rowHref?: (row: T) => string;
}

/**
 * Whether a click inside a row was meant for the row.
 *
 * A row full of Take payment and Invoice buttons is also a row you can click
 * to open — so anything that is itself a control keeps its own click, and only
 * the space between them opens the row. Text the user has selected is left
 * alone too: dragging across a phone number to copy it should not navigate.
 */
function opensTheRow(event: ReactMouseEvent<HTMLTableRowElement>): boolean {
  if (event.defaultPrevented || event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (window.getSelection()?.isCollapsed === false) return false;
  return !(event.target as HTMLElement).closest('a, button, input, select, textarea, label');
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  isError,
  onRetry,
  errorHeadline,
  errorDetail,
  empty,
  rowHref,
}: DataTableProps<T>): ReactElement {
  const navigate = useNavigate();

  if (isError) {
    return (
      <ErrorState headline={errorHeadline} detail={errorDetail} onRetry={onRetry} retryLabel="Load again" />
    );
  }

  if (isLoading) {
    // Rules where the rows will be, so the layout does not jump when they land.
    return (
      <div className="px-4 py-4" aria-busy="true">
        <span className="sr-only">Loading</span>
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className="h-9 border-b border-rule last:border-b-0" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return <EmptyState headline={empty.headline} detail={empty.detail} action={empty.action} />;
  }

  return (
    <div className="overflow-x-auto">
      <table>
        <thead>
          <tr className="border-b border-rule-strong">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={[
                  'whitespace-nowrap px-3 py-2 text-micro font-medium uppercase text-ink-muted',
                  column.numeric === true ? 'text-right' : 'text-left',
                  column.secondary === true ? 'hidden sm:table-cell' : '',
                ].join(' ')}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={
                rowHref === undefined
                  ? undefined
                  : (event) => {
                      if (opensTheRow(event)) navigate(rowHref(row));
                    }
              }
              className={[
                'border-b border-rule last:border-b-0',
                rowHref === undefined ? '' : 'cursor-pointer hover:bg-signal-wash',
              ].join(' ')}
            >
              {columns.map((column, index) => (
                <td
                  key={column.key}
                  className={[
                    'px-3 py-2 align-middle',
                    column.numeric === true ? 'tnum text-right' : '',
                    column.secondary === true ? 'hidden sm:table-cell' : '',
                  ].join(' ')}
                >
                  {index === 0 && rowHref !== undefined ? (
                    <Link to={rowHref(row)} className="font-medium text-ink underline-offset-2 hover:underline">
                      {column.render(row)}
                    </Link>
                  ) : (
                    column.render(row)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
